import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  GraduationCap,
  BookOpen,
  Activity,
  User,
  LogOut,
  Menu,
  X,
  FolderOpen,
  Users,
  Calendar,
  Search,
  FileText,
  Mic,
  ClipboardCheck,
} from 'lucide-react';
import { Button } from './ui/button';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/school-explorer', label: 'School Explorer', icon: Search },
  { path: '/colleges', label: 'My Schools', icon: GraduationCap },
  { path: '/essays', label: 'Writing', icon: BookOpen },
  { path: '/activities', label: 'Activities', icon: Activity },
  { path: '/documents', label: 'Documents', icon: FolderOpen },
  { path: '/recommenders', label: 'Recommendations', icon: Users },
  { path: '/timeline', label: 'Timeline', icon: Calendar },
  { path: '/resume', label: 'Resume', icon: FileText },
  { path: '/interview', label: 'Interview Prep', icon: Mic },
  { path: '/checklist', label: 'Submit Checklist', icon: ClipboardCheck },
  { path: '/profile', label: 'Profile', icon: User },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="app-layout">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:flex lg:w-60 lg:flex-col bg-white border-r border-[#E2E2E2]">
        <div className="flex h-16 items-center gap-2 px-6 border-b border-[#E2E2E2]">
          <GraduationCap className="h-6 w-6 text-[#0F392B]" strokeWidth={1.5} />
          <span className="font-semibold text-lg tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>Ivy League Consulting</span>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {user?.trial_active && !user?.subscription && (
            <div className="mb-3 mx-1 p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-lg" data-testid="trial-banner">
              <p className="text-[10px] font-semibold text-[#D4AF37] uppercase tracking-wide">Free Trial</p>
              <p className="text-xs text-muted-foreground mt-0.5">{user.trial_days_left} day{user.trial_days_left !== 1 ? 's' : ''} remaining</p>
              <Link to="/paywall" className="text-[10px] text-[#0F392B] font-medium hover:underline mt-1 block">Upgrade now</Link>
            </div>
          )}
          {navItems.map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                  active
                    ? 'bg-[#0F392B] text-white'
                    : 'text-muted-foreground hover:bg-[#F2F2F0] hover:text-foreground'
                }`}
              >
                <item.icon className="h-4 w-4" strokeWidth={1.5} />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-[#E2E2E2]">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-[#0F392B] flex items-center justify-center text-white text-xs font-medium">
              {(user?.first_name?.[0] || user?.email?.[0] || 'U').toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.first_name || 'Student'}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-sm text-muted-foreground hover:text-red-600"
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-[#E2E2E2] h-14 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <GraduationCap className="h-5 w-5 text-[#0F392B]" strokeWidth={1.5} />
          <span className="font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Ivy League Consulting</span>
        </div>
        <button onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/20" onClick={() => setMobileOpen(false)}>
          <div className="absolute top-0 left-0 w-64 h-full bg-white border-r border-[#E2E2E2] p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-6">
              <GraduationCap className="h-6 w-6 text-[#0F392B]" strokeWidth={1.5} />
              <span className="font-semibold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>Ivy League Consulting</span>
            </div>
            <nav className="space-y-1">
              {navItems.map((item) => {
                const active = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                      active ? 'bg-[#0F392B] text-white' : 'text-muted-foreground hover:bg-[#F2F2F0]'
                    }`}
                  >
                    <item.icon className="h-4 w-4" strokeWidth={1.5} />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
            <div className="mt-6 pt-4 border-t">
              <Button variant="ghost" className="w-full justify-start text-sm text-muted-foreground" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" /> Sign Out
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:pl-60 pt-14 lg:pt-0 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
